import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Cookies from "js-cookie";
import KakaoAuthPage from "./pages/KakaoAuthPage";
import KakaoAuthPage2 from "./pages/KakaoAuthPage2";
import VerificationPage from "./pages/VerificationPage";
import BondAgreementPage from "./pages/BondAgreementPage";
import LawyerAgreementPage from "./pages/LawyerAgreementPage";
import SignatureAgreementPage from "./pages/SignatureAgreementPage";
import IdCardCameraPage from "./pages/IdCardCameraPage";
import IdCardCropPage from "./pages/IdCardCropPage";
import IdCardSubmitPage from "./pages/IdCardSubmitPage";
import SubmissionConfirmPage from "./pages/SubmissionConfirmPage";
import CompletionPage from "./pages/CompletionPage";
import API_ENDPOINTS from "./config/api";

import "./App.css";

// 만료 시간이 있는 로컬 스토리지 도우미 함수
const storageWithExpiry = {
  set: (key, value, ttl) => {
    const now = new Date();
    const item = {
      value,
      expiry: now.getTime() + ttl,
    };
    localStorage.setItem(key, JSON.stringify(item));
  },
  get: (key) => {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;

    const item = JSON.parse(itemStr);
    const now = new Date();

    // 만료 시간 확인
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  },
  remove: (key) => {
    localStorage.removeItem(key);
  },
};

// 카카오톡 인앱뷰 안내 메시지 컴포넌트
const KakaoRedirectPage = () => {
  const [redirectComplete, setRedirectComplete] = useState(false);

  useEffect(() => {
    // 이미 리다이렉트를 시도한 경우에도 계속 페이지 유지
    const hasRedirected = storageWithExpiry.get("redirectAttempted") === true;
    if (hasRedirected) {
      setRedirectComplete(true);
      return;
    }

    // 리다이렉트 시도 기록 (5분 만료)
    const FIVE_MINUTES = 1 * 6 * 1000;
    storageWithExpiry.set("redirectAttempted", true, FIVE_MINUTES);

    // 페이지 로드 시 직접 외부 브라우저로 리다이렉트
    const urlToOpen =
      sessionStorage.getItem("originalUrl") || window.location.origin;
    const openExternalUrl = `kakaotalk://web/openExternal?url=${encodeURIComponent(
      urlToOpen
    )}`;

    // 약간의 지연 후 실행 (화면 렌더링 이후)
    const redirectTimer = setTimeout(() => {
      window.location.href = openExternalUrl;

      // 두 번째 방식 시도 (첫 번째가 실패할 경우)
      const fallbackTimer = setTimeout(() => {
        // 다른 방식의 리다이렉트 시도
        window.open(urlToOpen, "_system");
        setRedirectComplete(true);
      }, 1000);

      return () => clearTimeout(fallbackTimer);
    }, 300);

    return () => clearTimeout(redirectTimer);
  }, []);

  // 애니메이션 스타일 정의
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      padding: "0 20px",
      textAlign: "center",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
      background: "linear-gradient(to bottom, #f8f9fa, #e9ecef)",
      color: "#333",
    },
    card: {
      width: "100%",
      maxWidth: "380px",
      padding: "35px 30px",
      borderRadius: "16px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
      background: "white",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    iconContainer: {
      marginBottom: "25px",
    },
    icon: {
      width: "60px",
      height: "60px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      background: redirectComplete ? "#e6f7ff" : "#e6f7ff",
      color: redirectComplete ? "#0095f6" : "#0095f6",
      marginBottom: "5px",
    },
    title: {
      fontSize: "24px",
      fontWeight: "700",
      marginBottom: "20px",
      color: "#212529",
    },
    description: {
      fontSize: "16px",
      lineHeight: "1.7",
      marginBottom: "30px",
      color: "#495057",
      wordBreak: "keep-all",
    },
    loadingSpinner: {
      width: "22px",
      height: "22px",
      border: "3px solid #f0f0f0",
      borderTopColor: "#0095f6",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    footer: {
      fontSize: "14px",
      color: "#868e96",
      marginTop: "25px",
      opacity: "0.8",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconContainer}>
          <div style={styles.icon}>
            {redirectComplete ? (
              // 브라우저 아이콘
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 2v12h14V6H5zm7 3l4 4-4 4v-3H8v-2h4V9z"
                  fill="#0095f6"
                />
              </svg>
            ) : (
              // 화살표 아이콘
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.004 9.414l-8.607 8.607-1.414-1.414L14.589 8H7.004V6h11v11h-2V9.414z"
                  fill="#0095f6"
                />
              </svg>
            )}
          </div>
        </div>

        <h1 style={styles.title}>
          {redirectComplete
            ? "외부 브라우저에서 처리 중"
            : "외부 브라우저로 이동 중입니다"}
        </h1>

        <p style={styles.description}>
          {redirectComplete ? (
            <>
              공동소유 위임 동의 절차가 외부 브라우저에서 진행 중입니다.
              <br />
              카카오톡 앱 내에서는 진행할 수 없으며, 외부 브라우저에서 절차를
              완료해 주세요.
            </>
          ) : (
            <>
              공동소유 위임 동의 진행을 위해 외부 브라우저로 이동됩니다.
              <br />
              안전한 인증 및 처리를 위해 외부 브라우저에서 절차를 완료해 주세요.
            </>
          )}
        </p>

        {!redirectComplete && <div style={styles.loadingSpinner}></div>}

        {redirectComplete && (
          <div style={styles.footer}>이 창은 닫으셔도 됩니다</div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          body {
            margin: 0;
            padding: 0;
            background: #f8f9fa;
          }
        `}
      </style>
    </div>
  );
};

// 라우터 컴포넌트
function AppRoutes() {
  const location = useLocation();

  // 페이지 로드 시 모든 쿠키 초기화
  useEffect(() => {
    // 현재 페이지가 홈페이지(루트)인 경우에만 쿠키 초기화
    if (location.pathname === "/") {
      // 모든 쿠키 가져오기
      const allCookies = Cookies.get();

      // 모든 쿠키 삭제
      Object.keys(allCookies).forEach((cookieName) => {
        Cookies.remove(cookieName);
      });

      // API_ENDPOINTS에 정의된 쿠키 명시적으로 삭제
      const { KEYS } = API_ENDPOINTS.COOKIE_CONFIG;
      Object.values(KEYS).forEach((key) => {
        Cookies.remove(key);
      });

      // USER_INFO, VERIFICATION_CODE 쿠키 삭제
      Cookies.remove("USER_INFO");
      Cookies.remove("VERIFICATION_CODE");

      // 세션 스토리지 초기화 (리다이렉트 URL 정보 유지)
      const originalUrl = sessionStorage.getItem("originalUrl");
      sessionStorage.clear();
      if (originalUrl) {
        sessionStorage.setItem("originalUrl", originalUrl);
      }

      // 참고: 로컬 스토리지는 storageWithExpiry로 관리되므로 여기서 초기화하지 않음
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/verification" element={<KakaoAuthPage />} />
      <Route path="/kakao-auth-2" element={<KakaoAuthPage2 />} />
      <Route path="/" element={<VerificationPage />} />
      <Route path="/bond-agreement" element={<BondAgreementPage />} />

      <Route path="/signature-agreement" element={<SignatureAgreementPage />} />
      <Route path="/id-card-submit" element={<IdCardSubmitPage />} />
      <Route path="/id-card-camera" element={<IdCardCameraPage />} />
      <Route path="/id-card-crop" element={<IdCardCropPage />} />
      <Route path="/submission-confirm" element={<SubmissionConfirmPage />} />
      <Route path="/lawyer-agreement" element={<LawyerAgreementPage />} />
      <Route path="/completion" element={<CompletionPage />} />
      <Route path="/redirect-info" element={<KakaoRedirectPage />} />

      <Route path="/not-found" element={<div>Not Found Page</div>} />
    </Routes>
  );
}

function App() {
  const [isKakao, setIsKakao] = useState(false);

  // 카카오톡 인앱뷰 감지 및 외부 브라우저로 리다이렉트
  useEffect(() => {
    const isKakaoInAppBrowser = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      return userAgent.indexOf("kakaotalk") > -1;
    };

    const kakaoDetected = isKakaoInAppBrowser();
    setIsKakao(kakaoDetected);

    // 카카오톡 인앱뷰 감지 시 항상 리다이렉트 페이지로 이동
    if (kakaoDetected && !window.location.pathname.includes("/redirect-info")) {
      // 현재 URL 저장 (아직 리다이렉트 시도하지 않은 경우에만)
      if (storageWithExpiry.get("redirectAttempted") !== true) {
        const currentUrl = window.location.href;
        sessionStorage.setItem("originalUrl", currentUrl);
      }

      // 리다이렉트 정보 페이지로 이동 (항상)
      window.location.replace(window.location.origin + "/redirect-info");
    }
  }, []);

  return (
    <Router>
      <div className="app-container">
        <AppRoutes />
      </div>
    </Router>
  );
}

export default App;
